/**
 * Patient search (PHASE-0.md §2.3).
 *
 * **One field over names, personnummer and reservnummer at once.** No radio buttons,
 * no "search by" selector, no separate tab for a reservnummer — a clinician holding a
 * wristband types what is on it. The server decides nothing about which of the three
 * a term looks like either; see `PatientSearchTerm`.
 *
 * **Hits while typing**, which is what §2.3 asks for, but not a request per keystroke:
 * every search writes an audit row that can never be deleted (AGENTS.md §3, §4), so
 * the field waits {@link SEARCH_DEBOUNCE_MS} after the last keystroke and never asks
 * below {@link SEARCH_MINIMUM_LENGTH} characters. Twelve keystrokes of a personnummer
 * are one search and one row.
 *
 * The rows are the ward list's rows: the same columns in the same order, with the
 * safety flags in the row rather than behind a click (AGENTS.md §5, PHASE-0.md §3.4).
 * The only column that differs is the last one — the ward list knows where a patient
 * is because that is what it is a list of; a search has to say.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  PatientSearchError,
  SEARCH_DEBOUNCE_MS,
  SEARCH_MAXIMUM_LENGTH,
  SEARCH_MINIMUM_LENGTH,
  searchPatients,
  type PatientSearchFailure,
  type PatientSearchHit,
  type PatientSearchResults,
} from './patientApi'
import {
  guarded,
  patientAge,
  patientIdentifier,
  patientName,
  safetyBadges,
} from './patientDisplay'
import { OpenPatient } from './OpenPatient'
import { patientLocation, type PatientSearchTranslate } from './patientSearchDisplay'

type State =
  /** Nothing has been asked yet, or the term is too short to ask with. */
  | { readonly kind: 'idle' }
  | { readonly kind: 'searching' }
  | { readonly kind: 'found'; readonly results: PatientSearchResults }
  | { readonly kind: 'failed'; readonly failure: PatientSearchFailure }

function Row({
  hit,
  today,
  onOpenPatient,
  t,
}: {
  readonly hit: PatientSearchHit
  readonly today: Date
  readonly onOpenPatient: (patientId: string) => void
  readonly t: PatientSearchTranslate
}) {
  return (
    <tr>
      <td className="patient-search__name">
        <OpenPatient
          name={patientName(hit, t)}
          onOpen={() => onOpenPatient(hit.patientId)}
          t={t}
        />
      </td>
      <td className="patient-search__identifier">{patientIdentifier(hit, t)}</td>
      <td className="patient-search__age">{guarded(() => patientAge(hit, t, today), t)}</td>
      <td className="patient-search__location">{patientLocation(hit, t)}</td>
      <td className="patient-search__safety">
        <ul className="badges">
          {safetyBadges(hit, t).map((badge) => (
            <li key={badge.key} className={`badge badge--${badge.level}`}>
              {badge.label}
            </li>
          ))}
        </ul>
      </td>
    </tr>
  )
}

function Table({
  results,
  onOpenPatient,
  t,
}: {
  readonly results: PatientSearchResults
  readonly onOpenPatient: (patientId: string) => void
  readonly t: PatientSearchTranslate
}) {
  // One "today" for the whole render, so two rows cannot disagree about the date if
  // the table happens to render across midnight.
  const today = new Date()

  return (
    <table className="record-table patient-search__results">
      <caption className="visually-hidden">{t('patient:search.caption')}</caption>
      <thead>
        <tr>
          <th scope="col">{t('patient:column.name')}</th>
          <th scope="col">{t('patient:column.identifier')}</th>
          <th scope="col">{t('patient:column.age')}</th>
          <th scope="col">{t('patient:column.location')}</th>
          <th scope="col">{t('patient:column.safety')}</th>
        </tr>
      </thead>
      <tbody>
        {results.hits.map((hit) => (
          <Row
            key={hit.patientId}
            hit={hit}
            today={today}
            onOpenPatient={onOpenPatient}
            t={t}
          />
        ))}
      </tbody>
    </table>
  )
}

/**
 * The search screen.
 *
 * **The term is a prop, not local state**, and the reason is the trip into a patient and
 * back. This screen is unmounted while the overview is open (see `App.tsx`), so a local
 * term would be gone on return and a clinician would have to retype a twelve-digit
 * personnummer to get back to the list they were just looking at. `App` holds it across
 * that round trip and clears it when the clinician navigates away deliberately.
 *
 * Coming back does re-run the search, which writes a second audit row. That row is
 * truthful — the record really was searched again — and it is the cheaper of the two
 * costs (AGENTS.md §4: reads and searches are audited because that is how inappropriate
 * access happens, not as a side effect to be minimised).
 */
export function PatientSearchScreen({
  term,
  onTermChange,
  onOpenPatient,
}: {
  readonly term: string
  readonly onTermChange: (term: string) => void
  readonly onOpenPatient: (patientId: string) => void
}) {
  // `common` first: it is the default namespace and it is what lets this screen's `t`
  // render a safety badge through patientDisplay.ts.
  const { t } = useTranslation(['common', 'patient'])

  const [state, setState] = useState<State>({ kind: 'idle' })

  // Bumped by the retry button, so a retry is a new attempt at the same term rather
  // than an imperative refetch to keep in step with the state.
  const [attempt, setAttempt] = useState(0)
  const retry = useCallback(() => setAttempt((value) => value + 1), [])

  // The field, so the retry button and the hint can send focus back to it. A search
  // screen where the cursor is not in the field is a screen that needs a click before
  // it can be used.
  const field = useRef<HTMLInputElement>(null)

  const trimmed = term.trim()

  useEffect(() => {
    if (trimmed.length < SEARCH_MINIMUM_LENGTH) {
      // Below the floor nothing is asked and nothing is logged. This is not an error
      // state — it is the ordinary state of an empty field — so it must not look like
      // one, and in particular it must not read as "no such patient".
      setState({ kind: 'idle' })
      return
    }

    const controller = new AbortController()

    // Announced as searching immediately, while the request itself waits out the
    // debounce: the clinician has to see that the keystroke registered.
    setState({ kind: 'searching' })

    const timer = setTimeout(() => {
      searchPatients(trimmed, controller.signal).then(
        (results) => {
          if (!controller.signal.aborted) {
            setState({ kind: 'found', results })
          }
        },
        (cause: unknown) => {
          if (controller.signal.aborted) {
            return
          }
          setState({
            kind: 'failed',
            failure: cause instanceof PatientSearchError ? cause.failure : 'unavailable',
          })
        },
      )
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [trimmed, attempt])

  return (
    <main className="patient-search">
      <header className="patient-search__header">
        <h1>{t('patient:search.heading')}</h1>

        {/* A real label, not a placeholder. A placeholder disappears as soon as there
            is text in the field, which is exactly when somebody looks up to check
            what they are searching. */}
        <label className="patient-search__label" htmlFor="patient-search-term">
          {t('patient:search.label')}
        </label>

        <input
          id="patient-search-term"
          ref={field}
          className="patient-search__field"
          type="search"
          value={term}
          maxLength={SEARCH_MAXIMUM_LENGTH}
          // No spell-check, no autocorrect and no capitalisation: the browser must not
          // "improve" a personnummer or a Swedish surname it does not know.
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          // The start of a shift is somebody looking for a patient, so the field has
          // the cursor without a click.
          autoFocus
          aria-describedby="patient-search-hint"
          onChange={(event) => onTermChange(event.target.value)}
        />

        <p id="patient-search-hint" className="patient-search__hint">
          {t('patient:search.hint', { minimum: SEARCH_MINIMUM_LENGTH })}
        </p>
      </header>

      {/* Announced, because the result list replacing itself is what happens on this
          screen without the reader doing anything. `polite`, so it waits for a pause
          in typing rather than interrupting every keystroke. */}
      <div aria-live="polite">
        {state.kind === 'searching' && (
          <p className="patient-search__notice">{t('patient:search.searching')}</p>
        )}

        {state.kind === 'failed' && (
          <div className="patient-search__notice patient-search__notice--error" role="alert">
            <p>
              {state.failure === 'too-long'
                ? t('patient:search.tooLong', { maximum: SEARCH_MAXIMUM_LENGTH })
                : t('patient:search.failed')}
            </p>

            {/* No retry for a term the server refused on its length — trying the same
                too-long term again teaches nothing. Shortening it is the fix, so the
                cursor goes back to the field. */}
            {state.failure === 'unavailable' && (
              <button type="button" onClick={retry}>
                {t('patient:search.retry')}
              </button>
            )}
          </div>
        )}

        {state.kind === 'found' && (
          <>
            <p className="patient-search__count">
              {t('patient:search.count', { count: state.results.hits.length })}
            </p>

            {/* Said before the table, not after it: a clinician who stops reading at
                the last visible row has to have been told the list is cut short
                before they conclude the patient is not in the record. */}
            {state.results.truncated && (
              <p className="patient-search__notice patient-search__notice--warning" role="status">
                {t('patient:search.truncated', { limit: state.results.limit })}
              </p>
            )}

            {state.results.hits.length === 0 ? (
              <p className="patient-search__notice">{t('patient:search.empty')}</p>
            ) : (
              <Table results={state.results} onOpenPatient={onOpenPatient} t={t} />
            )}
          </>
        )}
      </div>
    </main>
  )
}
